// src/pages/EditBank.jsx
import React, { useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";

import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import {
  useGetBankByIdQuery,
  useUpdateBankMutation,
} from "../toolkitServices/bankApi";

const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);

const EditBank = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Companies
  const {
    data: companiesResp,
    isLoading: isCompaniesLoading,
    error: companiesError,
  } = useGetAllCompaniesQuery({ page: 1, limit: 50, search: "" });

  const companies =
    companiesResp?.data || companiesResp?.items || companiesResp?.rows || [];

  // Bank by id
  const {
    data: bankResp,
    isLoading: isBankLoading,
    error: bankError,
    refetch,
  } = useGetBankByIdQuery(id, { skip: !id });

  // Normalize bank object (API may return {data:{...}} or direct)
  const bank = useMemo(() => {
    const raw = bankResp?.data || bankResp?.item || bankResp;
    return raw || null;
  }, [bankResp]);

  // Update mutation
  const [updateBank, { isLoading: isUpdating, isSuccess, error: updateError }] =
    useUpdateBankMutation();

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      company: "",
      bankName: "",
      accountNumber: "",
      branch: "",
      bankCode: "",
      bankAddress: "",
      ifscCode: "",
    },
  });

  // Focus first field
  useEffect(() => {
    const rid = requestAnimationFrame(() =>
      setFocus("bankName", { shouldSelect: true })
    );
    return () => cancelAnimationFrame(rid);
  }, [setFocus]);

  // Prefill when bank loads
  useEffect(() => {
    if (!bank) return;

    reset({
      company: bank?.company?._id || bank?.company || "",
      bankName: pick(bank.bankName, bank.name, ""),
      accountNumber: pick(bank.accountNumber, bank.accNo, ""),
      branch: pick(bank.branch, ""),
      bankCode: pick(bank.bankCode, ""),
      bankAddress: pick(bank.bankAddress, ""),
      ifscCode: pick(bank.ifscCode, ""),
    });
  }, [bank, reset]);

  // Show fetch error
  useEffect(() => {
    if (bankError) {
      const msg =
        bankError?.data?.message ||
        bankError?.data?.msg ||
        "Failed to load bank details!";
      toast.error(msg);
    }
  }, [bankError]);

  // Update success
  useEffect(() => {
    if (isSuccess) {
      toast.success("Bank updated successfully!");
      navigate("/organisation/bank-list");
    }
  }, [isSuccess, navigate]);

  // Update error
  useEffect(() => {
    if (updateError) {
      const msg =
        updateError?.data?.message ||
        updateError?.data?.msg ||
        "Failed to update bank!";
      toast.error(msg);
    }
  }, [updateError]);

  const onSubmit = useCallback(
    async (form) => {
      const payload = {
        id, // required by updateBank mutation
        company: form.company,
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        branch: form.branch?.trim() || "",
        bankCode: form.bankCode?.trim() || "",
        bankAddress: form.bankAddress?.trim() || "",
        ifscCode: form.ifscCode?.trim().toUpperCase() || "",
      };

      try {
        await updateBank(payload).unwrap();
      } catch {
        /* toast handled */
      }
    },
    [id, updateBank]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const isBack = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      if (isSave) {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
      if (isBack) {
        e.preventDefault();
        navigate(-1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSubmit, navigate, onSubmit]);

  const loading = isBankLoading || isUpdating || isSubmitting;

  return (
    <div className="px-4 py-4">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to="/organisation/bank-master-list"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
            title="Back to Bank List"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              Edit Bank
            </h1>
            <p className="text-xs text-gray-500">
              Tip: Press{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">S</kbd> to Save,
              or{" "}
              <kbd className="px-1 bg-gray-100 rounded border">Ctrl</kbd>+
              <kbd className="px-1 bg-gray-100 rounded border">Backspace</kbd>{" "}
              to go back
            </p>
          </div>

          {/* Retry */}
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
            disabled={isBankLoading}
            title="Reload bank"
          >
            Reload
          </button>
        </div>
      </div>

      {/* Form Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-4xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {/* Company */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <select
              {...register("company", { required: "Company is required" })}
              aria-invalid={!!errors.company}
              disabled={isCompaniesLoading || companiesError}
              className={`${baseInput} ${errors.company ? errRing : okRing}`}
            >
              <option value="">
                {isCompaniesLoading
                  ? "Loading companies…"
                  : companiesError
                  ? "Failed to load companies"
                  : "Select a company"}
              </option>
              {companies.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.companyName}
                </option>
              ))}
            </select>
            {errors.company?.message && (
              <p className="text-xs text-red-600 mt-1">{errors.company.message}</p>
            )}
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register("bankName", {
                required: "Bank name is required",
                minLength: { value: 2, message: "Minimum 2 characters" },
              })}
              aria-invalid={!!errors.bankName}
              placeholder="Enter bank name"
              className={`${baseInput} ${errors.bankName ? errRing : okRing}`}
            />
            {errors.bankName?.message && (
              <p className="text-xs text-red-600 mt-1">{errors.bankName.message}</p>
            )}
          </div>

          {/* Account Number */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              {...register("accountNumber", {
                required: "Account number is required",
                minLength: { value: 6, message: "Invalid account number" },
                maxLength: { value: 25, message: "Max 25 characters allowed" },
              })}
              aria-invalid={!!errors.accountNumber}
              placeholder="Enter account number"
              inputMode="numeric"
              className={`${baseInput} ${
                errors.accountNumber ? errRing : okRing
              }`}
            />
            {errors.accountNumber?.message && (
              <p className="text-xs text-red-600 mt-1">
                {errors.accountNumber.message}
              </p>
            )}
          </div>

          {/* Branch */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Branch
            </label>
            <input
              {...register("branch")}
              placeholder="e.g. Andheri East"
              className={`${baseInput} ${okRing}`}
            />
          </div>

          {/* Bank Code */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Bank Code
            </label>
            <input
              {...register("bankCode", {
                maxLength: { value: 15, message: "Max 15 characters" },
              })}
              aria-invalid={!!errors.bankCode}
              placeholder="e.g. HDFC001"
              className={`${baseInput} ${errors.bankCode ? errRing : okRing}`}
            />
            {errors.bankCode?.message && (
              <p className="text-xs text-red-600 mt-1">{errors.bankCode.message}</p>
            )}
          </div>

          {/* IFSC */}
          <div>
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              IFSC Code
            </label>
            <input
              {...register("ifscCode", {
                maxLength: { value: 11, message: "IFSC is usually 11 characters" },
                pattern: {
                  value: /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/,
                  message: "Invalid IFSC format (e.g. HDFC0XXXXXXX)",
                },
              })}
              aria-invalid={!!errors.ifscCode}
              placeholder="e.g. HDFC0001234"
              className={`${baseInput} ${errors.ifscCode ? errRing : okRing} uppercase`}
            />
            {errors.ifscCode?.message && (
              <p className="text-xs text-red-600 mt-1">{errors.ifscCode.message}</p>
            )}
          </div>

          {/* Address */}
          <div className="md:col-span-3">
            <label className="block text-[13px] font-medium text-gray-700 mb-1">
              Bank Address
            </label>
            <input
              type="text"
              {...register("bankAddress", {
                maxLength: { value: 200, message: "Max 200 characters" },
              })}
              aria-invalid={!!errors.bankAddress}
              placeholder="Enter bank address"
              className={`${baseInput} ${errors.bankAddress ? errRing : okRing}`}
            />
            {errors.bankAddress?.message && (
              <p className="text-xs text-red-600 mt-1">
                {errors.bankAddress.message}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="md:col-span-3 flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 rounded-lg bg-[#9481ce] text-white font-medium shadow-sm hover:bg-[#7d6ec0] disabled:opacity-50"
            >
              {loading ? "Saving..." : "Update (Ctrl+S)"}
            </button>

            <Link
              to="/organisation/bank-list"
              className="px-5 py-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-medium"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditBank;
